import { Component, Host, h, Prop, Element, Watch, Listen } from '@stencil/core';
import { elementHost } from '../../utils/domHelpers';
import {
  showSlotContent,
  showSlotPreviousSiblingContent,
  slotHasAnyContent,
} from '../../utils/slotHelpers';
import { Layout, TooltipType } from './types';

const validationError = document.createElement('template');
validationError.innerHTML = '<div>This field is required</div>';

const errorSlotLightDOM = document.createElement('template');
errorSlotLightDOM.innerHTML = '<div slot="error"></div>';

// Set `error` attribute if there is an error message within the <aha-field> element.
function setErrorStatus(event: any) {
  const host = event.composedPath().pop().host;
  host.error = slotHasAnyContent(event.target);
  showSlotContent(event);
}

// Provide basic required validation for required input and textarea fields.
// Only tests for presence of a value, not whether or not that value is in the correct format.
function watchInput(event: any) {
  const host = event.currentTarget;
  if (!host.required) return;

  const selectro = host.querySelector('.SelectroV2__dropdown');
  // Don't do anything if the selectro is in this field.
  if (selectro) return;

  const input = event.target;

  // If this is not an <input type="text"> or <select>, then it's too complex for us to care about.
  if (!input.matches('input:not([type="hidden"]), select')) return;

  // If the change happens on a selectro input field, just ignore it. It is too complex for us to easily validate.
  if (input.classList.contains('selectro-trigger')) return;

  let emptyField = input && !input.value;
  if (input.classList.contains('aha-editor-body') && input.textContent !== '\ufeff') {
    // Extra check to take Aha Note Editor into account
    emptyField = false;
  }
  updateValidationError({ host, emptyField });
}

function updateValidationError({ host, emptyField }: { host: any; emptyField: boolean }) {
  // errorSlot lives in the light DOM.
  let errorSlot = host.querySelector('[slot="error"]');
  if (!errorSlot) {
    errorSlot = errorSlotLightDOM.content.cloneNode(true).firstChild;
    host.appendChild(errorSlot);
  }

  errorSlot.innerHTML = '';

  if (emptyField) {
    // Add very basic validation error.
    errorSlot.appendChild(validationError.content.cloneNode(true));
  }

  const slotchangeEvent = new Event('slotchange');
  errorSlot.assignedSlot.dispatchEvent(slotchangeEvent);
}

// Set a property if the <aha-field> has been focused into.
function watchFocusin(event: any) {
  const host = event.currentTarget;
  host.hasBeenFocused = true;
}

export interface AhaField extends HTMLAhaFieldElement {}

/**
 * A wrapper that manages form field labeling, error states, and hint text.
 */
/**
 * @slot DEFAULT - Contents that appear as the "body" of a field.
 * @slot label - Contents that appear above the field, which briefly describe the function of the field.
 * @slot hint - Contents that appear in a popup message next to the field's label. Typically used for short help text. Hint text is preferred over placeholders, because placeholders disappear when content is entered.
 * @slot help - Contents that appear below a field, and which describe its proper usage. Typically used for longer help text.
 * @slot error - Contents that appear below a field, and which describe any issues with the field after user has entered data. By default, error messages are colored red.
 *
 */
@Component({
  tag: 'aha-field',
  styleUrl: 'aha-field.css',
  shadow: true,
})
export class AhaField {
  @Element() element!: HTMLAhaFieldElement;

  /**
   * Whether or not a field is required. Provides a red `*` and a tooltip for accessibility.
   */
  @Prop({ reflect: true }) required!: boolean;

  /**
   * Whether field is laid out horizontally (default) or vertically.
   */
  @Prop({ reflect: true }) layout: Layout = 'horizontal';

  /**
   * Width of nested input field. Any valid CSS width value can be passed (50%, 20em, 10px, etc.).
   */
  @Prop({ reflect: true }) width!: string;

  /**
   * Safari has a weird race condition where the `onFocusout` event is fired when the `<aha-field>` component is mounted,
   * but this issue only occurs if the field mounts very quickly. In a small form with a relatively small number of input
   * elements the issue may occur, but in a larger form with a large number of elements it may not.
   * Use `hasBeenFocused` to keep track of whether or not this particular `<aha-field>` has ever been focused into. If
   * it has, then we can trust that it has legitimately been focused out of when a `blur` occurs. If it's not ever been
   * focused, then simply ignore the blur event when it happens.
   */
  private hasBeenFocused: boolean = false;

  /**
   * Control whether the field will try to focus into the first input element on click. You likely want to set to false
   * if you have more than one field in a row, and you want to be able to click into the second field without the first
   * field stealing focus.
   */
  @Prop({ reflect: true }) focusOnClick: boolean = true;

  /**
   * Whether or not a field has an error. `aria-invalid` is automatically added to child `input` tags when this
   * attribute is truthy.
   */
  @Prop({ reflect: true }) error: boolean = false;

  /**
   * Whether or not to reserve space for errors when an error is not visible.
   */
  @Prop({ reflect: true }) doNotReserveHeight: boolean = false;

  /**
   * Styling the tooltip for either a default style or a popover style
   */
  @Prop({ reflect: true }) tooltipType?: TooltipType = 'popover';

  // If `error` is true, then mark any input as aria-invalid.
  @Watch('error')
  private markAriaInvalid() {
    const inputs = this.element.querySelectorAll('input:not([type="hidden"]), textarea, select');
    inputs.forEach(input => {
      this.error
        ? input.setAttribute('aria-invalid', 'true')
        : input.removeAttribute('aria-invalid');
    });
  }

  /** Whether or not the first input, select, textarea in the body of the <aha-field> component should be focused
   * into when the component is done being mounted
   */
  @Prop({ reflect: true }) autofocus: boolean = false;

  componentWillLoad() {
    // Adding this enables :focus-visible to work with (for example) the <aha-tooltip> in the shadow dom.
    if (window.applyFocusVisiblePolyfill != null) {
      const root = (this as any).shadowRoot as ShadowRoot;
      window.applyFocusVisiblePolyfill(root);
    }

    if (this.error) this.markAriaInvalid();
  }

  private selectroSelectedChange(event: any) {
    if (!this.required) return;
    const selectro = this.element.querySelector('.SelectroV2');
    // Don't do anything if the selectro is not in this field.
    if (selectro && selectro === event.detail.element) {
      const emptyField = !event.detail.controller.selected.length;
      updateValidationError({ host: this.element, emptyField });
    }
  }

  componentDidLoad() {
    // Focus on <aha-field>'s first input element if the `autofocus` attribute is set.
    if (this.autofocus) {
      this.element
        .querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea, button')
        ?.focus();
    }
    // Watch for these selectro events to update the error state.
    [
      'selectro-v2:selected-change',
      'selectro-v2:selected',
      'selectro-v2:clear-selected-items',
      'selectro-v2:clear-selected',
    ].forEach(type => {
      document.addEventListener(type, this.selectroSelectedChange.bind(this));
    });
  }

  disconnectedCallback() {
    [
      'selectro-v2:selected-change',
      'selectro-v2:selected',
      'selectro-v2:clear-selected-items',
      'selectro-v2:clear-selected',
    ].forEach(type => {
      document.removeEventListener(type, this.selectroSelectedChange.bind(this));
    });
  }

  private shouldReserveSpace(error: boolean) {
    return error || !this.doNotReserveHeight;
  }

  private handleClick(event: MouseEvent) {
    // aha-editor needs control over focus and selection. Don't force
    // focus when clicking elements inside it.
    if ((event?.target as HTMLElement)?.closest('.aha-editor')) return;
    if ((event?.target as HTMLElement)?.closest('.aha-time-range-input')) return;
    if (!event?.currentTarget) return;
    if (!this.focusOnClick) return;

    const host = elementHost(event.currentTarget as Element);
    if (!host) return;

    host
      .querySelector<HTMLElement>(
        'input:not([type="hidden"]), select, textarea, *[contenteditable], .selectro-trigger',
      )
      ?.focus();
  }

  render() {
    return (
      <Host onInput={watchInput} onFocusout={watchInput} onFocusin={watchFocusin}>
        <div class="aha-field__column" part="aha-field__column">
          <label class="aha-field__label" onClick={this.handleClick}>
            <div class="aha-field__column">
              <div class="label aha-field__column">
                <div class="label-inner" part="aha-field__label-inner">
                  {this.required ? (
                    <div>
                      <div class="aha-field__required-container">
                        <slot name="label"></slot>
                        <span class="aha-field__required"></span>
                      </div>
                      <aha-tooltip strategy="absolute" type={this.tooltipType}>
                        Required field
                      </aha-tooltip>
                    </div>
                  ) : (
                    <slot name="label"></slot>
                  )}
                  <aha-tooltip-default-trigger class="hint" hidden></aha-tooltip-default-trigger>
                  <aha-tooltip hover-show hover-hide part="hint" type={this.tooltipType}>
                    <slot name="hint" onSlotchange={showSlotPreviousSiblingContent}></slot>
                  </aha-tooltip>
                </div>
              </div>

              <div class="aha-field__input" part="aha-field__input">
                <div
                  class="aha-field__width-container"
                  style={{
                    '--aha-field__input-width': this.width || '100%',
                  }}
                >
                  <slot></slot>
                </div>
                {/* <div class="additional"> */}
                <div
                  class={
                    this.shouldReserveSpace(this.error)
                      ? 'additional'
                      : 'additional--no-reserved-height'
                  }
                >
                  <slot name="error" onSlotchange={setErrorStatus} />
                  <slot name="help" onSlotchange={showSlotContent} />
                </div>
              </div>
            </div>
          </label>
        </div>
      </Host>
    );
  }
}
